import React, { useEffect, useMemo, useRef, useState } from 'react';

import { saveTeacherProfile } from '../../../api/personalizationApi';
import Dialog, { DialogBody, DialogHeader } from '../../../components/common/Dialog';
import ModalActions from '../../../components/common/ModalActions';
import Notice from '../../../components/common/Notice';
import { trackPersonalizationSurvey } from '../../../utils/eventTracker';
import SchoolAutocomplete from './SchoolAutocomplete';
import { schoolLevelFromKind } from './schoolLevel';
import './PersonalizationSurvey.css';

export const PERSONALIZATION_SURVEY_VERSION = 1;

export const PRIMARY_TASK_OPTIONS = [
  { value: 'record', label: '생기부 작성' },
  { value: 'lesson', label: '수업 자료·학습지' },
  { value: 'assessment', label: '평가·채점' },
  { value: 'document', label: '학교 행정문서' },
  { value: 'hwp', label: 'AI 한글' },
  { value: 'communication', label: '학부모 소통' },
  { value: 'ppt', label: 'PPT' },
  { value: 'image', label: '이미지' },
  { value: 'counseling', label: '상담' },
];

const SCHOOL_LEVEL_OPTIONS = [
  '유치원',
  '초등학교',
  '중학교',
  '고등학교',
  '특수학교',
  '기타',
];

const ROLE_OPTIONS = [
  '담임',
  '교과 전담',
  '부장·업무 담당',
  '상담·보건·사서',
  '관리자',
  '기타',
];

const SUBJECT_OPTIONS = [
  '전과목',
  '국어',
  '수학',
  '사회',
  '과학',
  '영어',
  '예체능',
  '정보·기술가정',
  '비교과·행정',
  '기타',
];

const STYLE_OPTIONS = [
  {
    value: 'concise',
    label: '핵심부터 간결하게',
    description: '필요한 내용부터 짧고 명확하게',
  },
  {
    value: 'practical',
    label: '바로 쓸 예시까지',
    description: '설명과 함께 복사해서 쓸 예시도',
  },
  {
    value: 'options',
    label: '선택지를 비교해서',
    description: '가능한 방법과 장단점을 함께',
  },
];

const gradeOptions = (schoolLevel) => {
  switch (schoolLevel) {
    case '유치원':
      return ['3세', '4세', '5세', '혼합 연령'];
    case '초등학교':
      return ['1~2학년', '3~4학년', '5~6학년', '전학년'];
    case '중학교':
    case '고등학교':
      return ['1학년', '2학년', '3학년', '전학년'];
    case '특수학교':
      return ['초등 과정', '중학 과정', '고등 과정', '전과정'];
    default:
      return ['전학년', '해당 없음'];
  }
};

const initialAnswers = (profile = {}) => ({
  school_name: profile.school_name || '',
  // 이미 나이스로 연결된 학교면 설문 저장이 연결을 끊지 않도록 코드를 함께 보낸다.
  // 이름을 다른 학교로 고치면 코드를 비워 수동 입력으로 저장된다.
  school_code: profile.school_code || '',
  office_code: profile.office_code || '',
  // 표시용(서버는 코드만 보고 나이스에서 다시 채운다)
  school_kind: profile.school_kind || '',
  school_address: profile.school_address || '',
  school_level: profile.school_level || '',
  subjects: [...(profile.subjects || [])],
  grades: [...(profile.grades || [])],
  roles: [...(profile.roles || [])],
  primary_tasks: profile.primary_tasks?.slice(0, 3) || [],
  response_style: profile.response_style || '',
});

/**
 * 30초 AI 맞춤 설문 (Dialog 프리미티브 위)
 * - 한 화면에 질문 하나. 단일 선택은 고르면 바로 다음 질문으로 넘어간다.
 * - 껍데기(스크림·포커스 트랩·ESC·바텀시트·닫기 버튼·스크롤 잠금)는 Dialog가 맡고,
 *   여기에는 진행 표시·질문·선택지·저장만 남긴다.
 * - props API 유지: { currentUser, initialProfile, onClose, onComplete, open }
 */
const PersonalizationSurvey = ({
  currentUser,
  initialProfile,
  onClose,
  onComplete,
  open,
}) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => initialAnswers(initialProfile));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const copyRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const advancingRef = useRef(false);

  // 학교를 나이스에서 골랐으면 학교급은 자동으로 채워지므로 그 질문은 건너뛴다.
  const schoolLinked = Boolean(answers.school_code && answers.office_code);
  const steps = useMemo(() => ([
    {
      key: 'school_name',
      title: '어느 학교에서 근무하세요?',
      description: '학교를 고르면 학사일정·급식·학교 정보를 AI가 알아서 참고해요.',
    },
    ...(schoolLinked ? [] : [{
      key: 'school_level',
      title: '어떤 학교급에서 근무하세요?',
      description: '더 정확한 추천과 AI 답변을 위해 수업 수준과 문서 예시를 맞춰요.',
    }]),
    {
      key: 'roles',
      title: '가장 가까운 역할은 무엇인가요?',
      description: '자주 필요한 업무 흐름을 먼저 제안해요.',
    },
    {
      key: 'grades',
      title: '주로 담당하는 학년은 어디인가요?',
      description: '문항과 예시의 난이도를 조절해요.',
    },
    {
      key: 'subjects',
      title: '주로 담당하는 과목·영역은 무엇인가요?',
      description: '학습지·PPT·평가 예시를 더 정확하게 골라요.',
    },
    {
      key: 'primary_tasks',
      title: '왓퀴즈에서 자주 할 일을 골라주세요',
      description: '최대 3개까지 고르면 자주 쓰는 도구에 맞춰 도와드려요.',
    },
    {
      key: 'response_style',
      title: '어떤 답변 방식이 가장 편하세요?',
      description: '현재 요청이 따로 있으면 언제나 현재 요청을 우선해요.',
    },
  ]), [schoolLinked]);

  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    setAnswers(initialAnswers(initialProfile));
    setSaving(false);
    setError('');
    advancingRef.current = false;
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current);
    }
    trackPersonalizationSurvey('started');
    return () => {
      if (advanceTimerRef.current) {
        window.clearTimeout(advanceTimerRef.current);
      }
      advancingRef.current = false;
    };
  }, [initialProfile, open]);

  // 질문이 바뀌면 질문 영역으로 포커스를 옮겨 스크린리더가 새 질문을 읽게 한다.
  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => {
      copyRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, step]);

  if (!open) return null;

  const current = steps[step];

  const handleClose = () => {
    if (saving) return;
    trackPersonalizationSurvey('closed', { step: step + 1 });
    onClose();
  };

  const selectSingle = (key, value) => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    const nextAnswers = {
      ...answers,
      [key]: Array.isArray(answers[key])
        ? (
          answers[key].includes(value)
            ? answers[key]
            : [value]
        )
        : value,
    };
    if (key === 'school_level' && value !== answers.school_level) {
      nextAnswers.grades = [];
    }
    setAnswers(nextAnswers);
    setError('');
    advanceTimerRef.current = window.setTimeout(() => {
      advancingRef.current = false;
      setStep((previous) => Math.min(previous + 1, steps.length - 1));
    }, 140);
  };

  const toggleTask = (value) => {
    const selected = answers.primary_tasks || [];
    if (selected.includes(value)) {
      setAnswers({
        ...answers,
        primary_tasks: selected.filter((item) => item !== value),
      });
      return;
    }
    if (selected.length >= 3) {
      setError('자주 쓰는 업무는 3개까지 선택할 수 있어요.');
      return;
    }
    setAnswers({
      ...answers,
      primary_tasks: [...selected, value],
    });
    setError('');
  };

  const goBack = () => {
    setError('');
    setStep((previous) => Math.max(0, previous - 1));
  };

  const save = async (event) => {
    event?.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const { school_kind: _kind, school_address: _address, ...profileAnswers } = answers;
      const payload = await saveTeacherProfile(currentUser, {
        ...profileAnswers,
        survey_completed: true,
        survey_version: PERSONALIZATION_SURVEY_VERSION,
      });
      trackPersonalizationSurvey('completed', {
        step: steps.length,
        taskCount: answers.primary_tasks.length,
        schoolNameProvided: Boolean(answers.school_name.trim()),
      });
      onComplete(payload);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const renderOptions = (options, key) => (
    <div className="personalization-survey-options">
      {options.map((option) => {
        const value = typeof option === 'string' ? option : option.value;
        const label = typeof option === 'string' ? option : option.label;
        const description = typeof option === 'string' ? '' : option.description;
        const selected = Array.isArray(answers[key])
          ? answers[key].includes(value)
          : answers[key] === value;
        return (
          <button
            aria-pressed={selected}
            className={`personalization-survey-option ${selected ? 'is-selected' : ''}`}
            key={value}
            onClick={() => selectSingle(key, value)}
            type="button"
          >
            <span>{label}</span>
            {description && <small>{description}</small>}
          </button>
        );
      })}
    </div>
  );

  // 하단 액션: 채워진 버튼은 단계마다 최대 하나. 단일 선택 단계는 고르면 바로 넘어가므로 없다.
  let primaryAction = null;
  if (current.key === 'primary_tasks') {
    primaryAction = {
      label: '다음으로',
      onClick: () => {
        // 3개 초과 안내가 다음 질문까지 따라가지 않게 지운다
        setError('');
        setStep(step + 1);
      },
      disabled: answers.primary_tasks.length === 0,
    };
  } else if (current.key === 'school_name') {
    primaryAction = {
      label: '다음으로',
      onClick: () => {
        setError('');
        setStep(step + 1);
      },
    };
  } else if (current.key === 'response_style') {
    primaryAction = {
      label: '맞춤 설정 저장하기',
      onClick: save,
      loading: saving,
      disabled: !answers.response_style,
    };
  }
  const backAction = step > 0
    ? { label: '이전', onClick: goBack, disabled: saving }
    : null;
  const footer = (error || primaryAction || backAction) ? (
    <div className="personalization-survey-footer">
      {error && (
        <Notice compact testId="personalization-survey-error" variant="error">
          {error}
        </Notice>
      )}
      {(primaryAction || backAction) && (
        <ModalActions
          primary={primaryAction}
          tertiary={backAction}
          testId="personalization-survey-actions"
        />
      )}
    </div>
  ) : null;

  return (
    <Dialog
      aria-label="AI 맞춤 설정"
      closeLabel="맞춤 설정 닫기"
      dismissible={!saving}
      flush
      footer={footer}
      onClose={handleClose}
      open
      panelClassName="personalization-survey"
      size="md"
      testId="personalization-survey"
    >
      <div className="personalization-survey-top">
        <span className="personalization-survey-step">{step + 1} / {steps.length}</span>
      </div>
      <div
        aria-hidden="true"
        className="personalization-survey-progress"
      >
        <span style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
      </div>

      <div
        aria-live="polite"
        className="personalization-survey-copy"
        ref={copyRef}
        tabIndex="-1"
      >
        <DialogHeader
          description={current.description}
          descriptionId="personalization-survey-description"
          title={current.title}
          titleId="personalization-survey-title"
        />
      </div>

      <DialogBody className="personalization-survey-content">
        {current.key === 'school_level'
          && renderOptions(SCHOOL_LEVEL_OPTIONS, 'school_level')}
        {current.key === 'roles'
          && renderOptions(ROLE_OPTIONS, 'roles')}
        {current.key === 'grades'
          && renderOptions(gradeOptions(answers.school_level), 'grades')}
        {current.key === 'subjects'
          && renderOptions(SUBJECT_OPTIONS, 'subjects')}
        {current.key === 'response_style'
          && renderOptions(STYLE_OPTIONS, 'response_style')}

        {current.key === 'primary_tasks' && (
          <div className="personalization-survey-options is-compact">
            {PRIMARY_TASK_OPTIONS.map((option) => {
              const selected = answers.primary_tasks.includes(option.value);
              return (
                <button
                  aria-pressed={selected}
                  className={`personalization-survey-option ${selected ? 'is-selected' : ''}`}
                  key={option.value}
                  onClick={() => toggleTask(option.value)}
                  type="button"
                >
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {current.key === 'school_name' && (
          <div className="personalization-survey-school">
            <label
              className="personalization-survey-school-label"
              htmlFor="personalization-school-name"
            >
              학교 이름 <span>선택</span>
            </label>
            <SchoolAutocomplete
              autoFocus
              currentUser={currentUser}
              disabled={saving}
              inputId="personalization-school-name"
              linked={schoolLinked ? {
                school_code: answers.school_code,
                office_code: answers.office_code,
                school_kind: answers.school_kind,
                school_address: answers.school_address,
              } : null}
              onChangeName={(nextName) => {
                const keepLink = nextName.trim() === String(initialProfile?.school_name || '').trim();
                // 원래 이름으로 되돌리면 저장돼 있던 코드도 함께 복구한다.
                setAnswers((prev) => ({
                  ...prev,
                  school_name: nextName,
                  school_code: keepLink ? (initialProfile?.school_code || '') : '',
                  office_code: keepLink ? (initialProfile?.office_code || '') : '',
                  school_kind: keepLink ? (initialProfile?.school_kind || '') : '',
                  school_address: keepLink ? (initialProfile?.school_address || '') : '',
                }));
              }}
              onSelect={(school) => {
                const level = schoolLevelFromKind(school.school_kind);
                setAnswers((prev) => ({
                  ...prev,
                  school_name: school.school_name,
                  school_code: school.school_code,
                  office_code: school.office_code,
                  school_kind: school.school_kind || '',
                  school_address: school.school_address || '',
                  school_level: level || prev.school_level,
                  grades: level && level !== prev.school_level ? [] : prev.grades,
                }));
                setError('');
              }}
              onUnlink={() => setAnswers((prev) => ({
                ...prev,
                school_code: '',
                office_code: '',
                school_kind: '',
                school_address: '',
              }))}
              value={answers.school_name}
            />
            <p className="personalization-survey-privacy">
              {schoolLinked
                ? '학사일정·급식·학교 정보를 참고해서 답해드릴게요. 일반 대화에는 학교명을 노출하지 않아요.'
                : '일반 대화에는 학교명을 노출하지 않고, 기관명 자리가 필요한 문서에서만 사용해요.'}
            </p>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
};

export default PersonalizationSurvey;
